import type { AiModerationTrace, ModerationHistoryEntry } from '../types/moderation';
import { formatDateTime } from '../lib/format';
import EmptyState from './ui/EmptyState';

interface ModerationTimelineProps {
  history: ModerationHistoryEntry[] | undefined;
  aiAnalysis?: AiModerationTrace[];
}

const ACTION_CLASS: Record<string, string> = {
  approved: 'timeline-action--green',
  rejected: 'timeline-action--red',
  withdrawn: 'timeline-action--grey',
  pending_review: 'timeline-action--amber',
  submitted: 'timeline-action--blue',
  ai_auto_approved: 'timeline-action--green',
  ai_auto_rejected: 'timeline-action--red',
  ai_flagged: 'timeline-action--amber',
  metadata_updated: 'timeline-action--blue',
};

const ACTION_LABEL: Record<string, string> = {
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  pending_review: 'Pending',
  submitted: 'Submitted',
  ai_auto_approved: 'AI: auto-approved',
  ai_auto_rejected: 'AI: auto-rejected',
  ai_flagged: 'AI: human review',
  metadata_updated: 'Metadata updated',
};

const AI_ACTIONS = new Set(['ai_auto_approved', 'ai_auto_rejected', 'ai_flagged']);

function extractTraceId(note: string | undefined): string | undefined {
  if (!note) return undefined;
  const match = note.match(/trace=([0-9a-f-]{8,})/i);
  return match?.[1];
}

interface AiTraceDetailsProps {
  trace: AiModerationTrace;
}

function AiTraceDetails({ trace }: AiTraceDetailsProps) {
  const safety = trace.signals.safety;
  return (
    <details className="timeline-ai-details" data-testid="timeline-ai-details">
      <summary>Reasoning &amp; signals</summary>
      <div className="timeline-ai-details__body">
        <div className="timeline-ai-details__row">
          <span className="timeline-ai-details__label">Model:</span>
          <span>{trace.modelVersion}</span>
        </div>
        <div className="timeline-ai-details__row">
          <span className="timeline-ai-details__label">Confidence:</span>
          <span>{Math.round(trace.confidence * 100)}%</span>
        </div>
        <div className="timeline-ai-details__row">
          <span className="timeline-ai-details__label">Safety:</span>
          <span>
            {safety.graphicSexual.detected && '⚠ graphic sexual '}
            {safety.graphicViolence.detected && '⚠ graphic violence '}
            {safety.spam.detected && '⚠ spam '}
            {!safety.graphicSexual.detected &&
              !safety.graphicViolence.detected &&
              !safety.spam.detected &&
              'clean'}
          </span>
        </div>
        {trace.signals.pii.detected && (
          <div className="timeline-ai-details__row">
            <span className="timeline-ai-details__label">PII spans:</span>
            <ul className="timeline-ai-details__pii">
              {trace.signals.pii.spans.map((s, i) => (
                <li key={i}>
                  <code>{s.text}</code> ({s.type} in {s.sourceField})
                </li>
              ))}
            </ul>
          </div>
        )}
        {trace.signals.duplicate.isDuplicate && (
          <div className="timeline-ai-details__row">
            <span className="timeline-ai-details__label">Duplicate:</span>
            <span>
              matches submission {trace.signals.duplicate.similarSubmissionId}
              {trace.signals.duplicate.hammingDistance != null
                ? ` (hamming ${trace.signals.duplicate.hammingDistance})`
                : ''}
            </span>
          </div>
        )}
        {trace.thinking && (
          <div className="timeline-ai-details__row">
            <span className="timeline-ai-details__label">Reasoning:</span>
            <div className="timeline-ai-details__thinking-wrap">
              {/* F-304: copy button so engineers can paste the AI's
                  reasoning into a Slack thread without selecting it
                  by hand. Mirrors the metadata-section copy button
                  pattern. */}
              <button
                type="button"
                className="metadata-section__copy-btn timeline-ai-details__copy"
                onClick={() => {
                  if (typeof navigator !== 'undefined' && navigator.clipboard) {
                    void navigator.clipboard.writeText(trace.thinking);
                  }
                }}
                aria-label="Copy AI reasoning to clipboard"
                data-testid={`copy-reasoning-${trace.id}`}
              >
                Copy
              </button>
              <pre className="timeline-ai-details__thinking">{trace.thinking}</pre>
            </div>
          </div>
        )}
        <div className="timeline-ai-details__row timeline-ai-details__row--meta">
          <span>tokens: {trace.inputTokens}↓ {trace.outputTokens}↑</span>
          <span>trace id: <code>{trace.id}</code></span>
        </div>
      </div>
    </details>
  );
}

export default function ModerationTimeline({ history, aiAnalysis }: ModerationTimelineProps) {
  if (!history || history.length === 0) {
    return (
      <EmptyState
        testId="timeline-empty"
        title="No moderation history yet."
        description="When a moderator approves, rejects, or leaves a note, it will appear here."
      />
    );
  }

  const traces = aiAnalysis || [];

  return (
    <ol className="moderation-timeline" data-testid="moderation-timeline">
      {history.map((entry, idx) => {
        const isAi = AI_ACTIONS.has(entry.action);
        const traceId = isAi ? extractTraceId(entry.note) : undefined;
        const trace = traceId ? traces.find((t) => t.id === traceId) : undefined;
        return (
          <li key={`${entry.timestamp}-${idx}`} className="timeline-entry">
            <span
              className={`timeline-action ${ACTION_CLASS[entry.action] || 'timeline-action--grey'}`}
              data-action={entry.action}
            >
              {ACTION_LABEL[entry.action] || entry.action}
            </span>
            <div className="timeline-meta">
              <span className="timeline-user">{entry.userEmail}</span>
              <span className="timeline-time">{formatDateTime(entry.timestamp)}</span>
            </div>
            {entry.note && <p className="timeline-note">{entry.note}</p>}
            {trace && <AiTraceDetails trace={trace} />}
          </li>
        );
      })}
    </ol>
  );
}
